import React, {
  useEffect,
  useState,
  useMemo,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import dayjs from 'dayjs';
import {
  AppBar,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Toolbar,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import DeleteIcon from '@mui/icons-material/Delete';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HouseIcon from '@mui/icons-material/House';
import SearchIcon from '@mui/icons-material/Search';
import { DataGrid } from 'react-data-grid';
import ReactHtmlParser from 'react-html-parser';
import { toast } from 'react-toastify';
import _ from 'lodash';

import { creators as StrategyActions } from '../../Reducers/Strategy';
import { creators as CompanySettingsActions } from '../../Reducers/CompanySettings';
import StrategyExportButton from '../../Components/StrategyExportButton';
import {
  FilterContext,
  rowKeyGetter,
  FilterRenderer,
  inputStopPropagation,
  selectStopPropagation,
  handleCopy,
  renderTextEditor,
} from '../../Components/ReactDataGridProps';
import strategyApi from '../../Api/ApiStrategy';
import appdata from '../../Services/appdata';
import { strategyExportToPdf } from '../../Utils/Functions';
import ObjectiveTable from './ObjectiveTable';
import MileStoneTable from './MileStoneTable';
import './style.css';

const planTypes = [
  { id: 'Objective', value: 'Objective' },
  { id: 'Milestones', value: 'Milestones' },
];
const intervalTypes = [
  { id: 'Years', value: 'Years' },
  { id: 'Half years', value: 'Half years' },
  { id: 'Quarters', value: 'Quarters' },
];

const intervalMonths = {
  Years: 12,
  'Half years': 6,
  Quarters: 3,
};

const toDateOnly = (value) => {
  if (!value) return null;

  if (typeof value === 'string') {
    const dateOnlyMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (dateOnlyMatch) {
      return new Date(
        Number(dateOnlyMatch[1]),
        Number(dateOnlyMatch[2]) - 1,
        Number(dateOnlyMatch[3]),
      );
    }
  }

  const date = value.$d ? new Date(value.$d) : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

const formatDateForApi = (value) => {
  const date = toDateOnly(value);
  if (!date) return value;

  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const addMonths = (value, months) => {
  const date = toDateOnly(value);
  if (!date) return null;

  const day = date.getDate();
  const result = new Date(date);
  result.setDate(1);
  result.setMonth(result.getMonth() + months);
  const lastDayOfTargetMonth = new Date(
    result.getFullYear(),
    result.getMonth() + 1,
    0,
  ).getDate();
  result.setDate(Math.min(day, lastDayOfTargetMonth));
  return result;
};

const getIntervalMonths = (interval) => intervalMonths[interval] || 6;

const getInclusiveIntervalCount = (startValue, endValue, interval) => {
  const startDate = toDateOnly(startValue);
  const endDate = toDateOnly(endValue);
  const monthStep = getIntervalMonths(interval);

  if (!startDate || !endDate || startDate > endDate) return 0;

  let count = 0;
  for (
    let cursor = new Date(startDate);
    cursor <= endDate;
    cursor = addMonths(cursor, monthStep)
  ) {
    count += 1;
  }

  return count;
};

const getIntervalsBeforeDate = (startValue, beforeValue, interval) => {
  const startDate = toDateOnly(startValue);
  const beforeDate = toDateOnly(beforeValue);
  const monthStep = getIntervalMonths(interval);

  if (!startDate || !beforeDate || startDate >= beforeDate) return 0;

  let count = 0;
  for (
    let cursor = new Date(startDate);
    cursor < beforeDate;
    cursor = addMonths(cursor, monthStep)
  ) {
    count += 1;
  }

  return count;
};

const buildDateChangeImpact = (row, field, newValue) => {
  const originalStartDate = toDateOnly(row.StartDate);
  const originalEndDate = toDateOnly(row.EndDate);
  const newDate = toDateOnly(newValue);
  const interval = row.Interval;

  if (!originalStartDate || !originalEndDate || !newDate) {
    return {
      currentIntervalCount: 0,
      nextIntervalCount: 0,
      removeFirstInterval: false,
      prependIntervalCount: 0,
      shouldConfirm: false,
      messageLines: ['The selected date could not be read. Please choose a valid date.'],
    };
  }

  const nextStartDate = field === 'StartDate' ? newDate : originalStartDate;
  const nextEndDate = field === 'EndDate' ? newDate : originalEndDate;
  const currentIntervalCount = getInclusiveIntervalCount(
    originalStartDate,
    originalEndDate,
    interval,
  );
  const nextIntervalCount = getInclusiveIntervalCount(
    nextStartDate,
    nextEndDate,
    interval,
  );
  const removeFirstInterval = field === 'StartDate' && newDate > originalStartDate;
  const prependIntervalCount =
    field === 'StartDate' && newDate < originalStartDate
      ? getIntervalsBeforeDate(newDate, originalStartDate, interval)
      : 0;
  const removedFromStart = removeFirstInterval
    ? Math.max(currentIntervalCount - nextIntervalCount, 0)
    : 0;
  const addedToEnd =
    field === 'EndDate' && newDate > originalEndDate
      ? Math.max(nextIntervalCount - currentIntervalCount, 0)
      : 0;
  const removedFromEnd =
    field === 'EndDate' && newDate < originalEndDate
      ? Math.max(currentIntervalCount - nextIntervalCount, 0)
      : 0;
  const intervalLabel = interval || 'interval';
  const messageLines = [
    `Current range has ${currentIntervalCount} ${intervalLabel} row${currentIntervalCount === 1 ? '' : 's'}.`,
    `New range will have ${nextIntervalCount} ${intervalLabel} row${nextIntervalCount === 1 ? '' : 's'}.`,
  ];

  if (prependIntervalCount > 0) {
    messageLines.push(
      `${prependIntervalCount} blank ${intervalLabel} row${prependIntervalCount === 1 ? '' : 's'} will be added at the start. Existing milestone data will shift right.`,
    );
  }

  if (removedFromStart > 0) {
    messageLines.push(
      `${removedFromStart} ${intervalLabel} row${removedFromStart === 1 ? '' : 's'} will be removed from the start.`,
    );
  }

  if (addedToEnd > 0) {
    messageLines.push(
      `${addedToEnd} blank ${intervalLabel} row${addedToEnd === 1 ? '' : 's'} will be added at the end.`,
    );
  }

  if (removedFromEnd > 0) {
    messageLines.push(
      `${removedFromEnd} ${intervalLabel} row${removedFromEnd === 1 ? '' : 's'} will be removed from the end.`,
    );
  }

  if (nextIntervalCount <= 0) {
    messageLines.push('No milestone intervals would remain in the selected date range.');
  }

  return {
    currentIntervalCount,
    nextIntervalCount,
    removeFirstInterval,
    prependIntervalCount,
    shouldConfirm:
      currentIntervalCount !== nextIntervalCount ||
      removeFirstInterval ||
      prependIntervalCount > 0,
    messageLines,
  };
};

const buildIntervalChangeImpact = (row, newInterval) => {
  const currentIntervalCount = getInclusiveIntervalCount(
    row.StartDate,
    row.EndDate,
    row.Interval,
  );
  const nextIntervalCount = getInclusiveIntervalCount(
    row.StartDate,
    row.EndDate,
    newInterval,
  );

  return {
    currentIntervalCount,
    nextIntervalCount,
    messageLines: [
      `Current layout uses ${row.Interval || 'the current interval'} with ${currentIntervalCount} period${currentIntervalCount === 1 ? '' : 's'}.`,
      `New layout will use ${newInterval} with ${nextIntervalCount} period${nextIntervalCount === 1 ? '' : 's'}.`,
      'Preserve & Merge keeps milestone content and redistributes it across the new periods.',
      'Start Fresh removes the existing milestone-period rows for this plan.',
    ],
  };
};


function getComparator(sortColumn) {
  switch (sortColumn) {
    case 'id':
      return (a, b) => {
        return a[sortColumn] - b[sortColumn];
      };
    case 'Name':
    case 'StartDate':
    case 'EndDate':
    case 'PlanType':
    case 'Interval':
      return (a, b) => {
        return a[sortColumn]?.localeCompare(b[sortColumn]);
      };
    default:
      throw new Error(`unsupported sortColumn: "${sortColumn}"`);
  }
}

function Strategy() {
  const companySettings = useSelector(
    (state) => state.CompanySettings.company_settings,
  );
  const planHeaders = useSelector((state) => state.Strategy.planheaders);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [companyLogo, setCompanyLogo] = useState(null);
  const [byLine, setByLine] = useState('');
  const [selectedPlanId, setSelectedPlanId] = useState(0);
  const [selParentId, setSelParentId] = useState(-1);
  const [selectedRowId, setSelectedRowId] = useState(-1);
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({
    Name: '',
    PlanType: 'All',
    Interval: 'All',
    DimPlanHeaderKey_ParentID: 'All',
    Status: false,
    enabled: false,
  });
  const [sortColumns, setSortColumns] = useState([]);
  const [showStructureModal, setShowStructureModal] = useState(false);
  const [pendingStructureChange, setPendingStructureChange] = useState(null);

  useEffect(() => {
    dispatch(CompanySettingsActions.companySettingsGetAllCompanySettings());
    dispatch(StrategyActions.strategyGetPlanheaders());
  }, [dispatch]);

  useEffect(() => {
    setRows(planHeaders);
  }, [planHeaders]);

  useEffect(() => {
    getCompanyInformation();
    // eslint-disable-next-line
  }, [companySettings]);

  const getCompanyInformation = () => {
    let company_id = parseInt(sessionStorage.getItem('company_org_id'));
    let company = null;
    if (company_id) {
      if (companySettings)
        company = companySettings.find((company) => company.id === company_id);
    } else {
      if (companySettings) {
        company = companySettings[0];
        sessionStorage.setItem('company_org_id', companySettings[0].id);
      }
    }

    if (company) {
      setCompanyLogo(company.ImageData);
      setByLine(company.MissionStatement);
    }
  };

  const onSignout = () => {
    appdata.localClear();
    appdata.clear();
  };

  const handleTableRoutes = (ev, newValue) => {
    if (newValue === 0) {
      setSelectedPlanId(0);
      setSelParentId(-1);
    } else {
      setSelectedPlanId(activeSortedRows[newValue - 1]?.id || 0);
      setSelParentId(-1);
    }
  };

  const handleShowChildren = (parentId) => {
    let newPlan = -1;
    rows &&
      rows.forEach((row) => {
        if (
          row['DimPlanHeaderKey_ParentID'] === selectedPlanId &&
          newPlan === -1
        ) {
          newPlan = row['id'];
        }
      });
    if (newPlan > -1) {
      setSelParentId(parentId);
      setSelectedPlanId(newPlan);
    } else {
      toast.info("It doesn't have any children");
    }
  };

  const handleSelParentIdChange = (val) => {
    if (selParentId === val) {
      setSelParentId(-1);
    } else {
      setSelParentId(val);
    }
  };

  const onRouteBack = () => {
    setSelectedPlanId(0);
  };

  const onClickedAdd = async () => {
    await strategyApi
      .addStrategyPlanHeader()
      .then((res) => {
        if (res.status === 200)
          dispatch(StrategyActions.strategyGetPlanheaders());
        else toast.error(res.message);
      })
      .catch((error) => toast.error(error.message));
  };

  const handleRowChange = (updatedRows, field) => {
    setRows(updatedRows);
    let updated_data = {
      rowIds: [updatedRows[field.indexes[0]].id],
      updated: {
        [field.column.key]: updatedRows[field.indexes[0]][field.column.key],
      },
    };
    updateStrategyPlanHeader(updated_data);
  };

  const handleCellClick = (rowData) => {
    if (selectedRowId !== rowData.row.id) {
      setSelectedRowId(rowData.row.id);
    }
  };

  const handlePaste = ({
    sourceColumnKey,
    sourceRow,
    targetColumnKey,
    targetRow,
  }) => {
    const incompatibleColumns = [
      'id',
      'OrderId',
      'PlanType',
      'Interval',
      'DimPlanHeaderKey_ParentID',
    ];
    if (
      incompatibleColumns.includes(targetColumnKey) ||
      incompatibleColumns.includes(sourceColumnKey)
    ) {
      return targetRow;
    }

    return { ...targetRow, [targetColumnKey]: sourceRow[sourceColumnKey] };
  };

  const onClickedDelete = async () => {
    if (selectedRowId < 0) return;
    await strategyApi
      .deleteStrategyPlanHeader(selectedRowId)
      .then((res) => {
        if (res.status === 200) {
          dispatch(StrategyActions.strategyGetPlanheaders());
          setSelectedRowId(-1);
        } else toast.error(res.message);
      })
      .catch((error) => toast.error(error.message));
  };

  const toggleFilters = () => {
    setFilters((filters) => ({
      ...filters,
      enabled: !filters.enabled,
    }));
  };

  const handleOrderUp = async (rowId) => {
    let selectedIndex = _.findIndex(rows, { id: rowId });
    if (selectedIndex <= 0) return;

    let updated_data = {
      rowIds: [rowId],
      updated: {
        OrderId: rows[selectedIndex]['OrderId'] - 1,
      },
    };

    await strategyApi
      .updateStrategyPlanHeader(updated_data)
      .then((res) => {
        if (res.status === 200) {
        } else toast.error(res.message);
      })
      .catch((error) => toast.error(error.message));

    let upRowId = rows[selectedIndex - 1]['id'];
    updated_data = {
      rowIds: [upRowId],
      updated: {
        OrderId: rows[selectedIndex - 1]['OrderId'] + 1,
      },
    };
    updateStrategyPlanHeader(updated_data);
  };

  const handleOrderDown = async (rowId) => {
    let selectedIndex = _.findIndex(rows, { id: rowId });
    if (selectedIndex < 0 || selectedIndex >= rows.length - 1) return;

    let updated_data = {
      rowIds: [rowId],
      updated: {
        OrderId: rows[selectedIndex]['OrderId'] + 1,
      },
    };

    await strategyApi
      .updateStrategyPlanHeader(updated_data)
      .then((res) => {
        if (res.status === 200) {
        } else toast.error(res.message);
      })
      .catch((error) => toast.error(error.message));

    let downRowId = rows[selectedIndex + 1]['id'];
    updated_data = {
      rowIds: [downRowId],
      updated: {
        OrderId: rows[selectedIndex + 1]['OrderId'] - 1,
      },
    };
    updateStrategyPlanHeader(updated_data);
  };

  const updateStrategyPlanHeader = async (updated_data, structureMode = null) => {
    try {
      const res = await strategyApi.updateStrategyPlanHeader(
        updated_data,
        structureMode,
      );
      if (res.status === 200) {
        dispatch(StrategyActions.strategyGetPlanheaders());
        return true;
      }
      toast.error(res.message);
      dispatch(StrategyActions.strategyGetPlanheaders());
      return false;
    } catch (error) {
      toast.error(error.message);
      dispatch(StrategyActions.strategyGetPlanheaders());
      return false;
    }
  };

  const handleDateChange = (newValue, props, field) => {
    const selectedDate = toDateOnly(newValue);
    if (!selectedDate) return;

    const formattedDate = formatDateForApi(selectedDate);
    if (formatDateForApi(props.row[field]) === formattedDate) return;

    const impact = buildDateChangeImpact(props.row, field, selectedDate);
    setPendingStructureChange({
      kind: 'date',
      row: props.row,
      field,
      newValue: formattedDate,
      impact,
    });
    setShowStructureModal(true);
  };

  const handleStartDateChange = (newValue, props) => {
    handleDateChange(newValue, props, 'StartDate');
  };

  const handleEndDateChange = (newValue, props) => {
    handleDateChange(newValue, props, 'EndDate');
  };

  const handlePlanTypeChange = (newValue, props) => {
    if (newValue === props.row.PlanType) return;
    setPendingStructureChange({
      kind: 'planType',
      row: props.row,
      field: 'PlanType',
      newValue,
      impact: {
        messageLines: [
          `This changes the plan from ${props.row.PlanType || 'an unset type'} to ${newValue}.`,
          'Existing objective and milestone data will be retained in the database. Nothing will be deleted.',
          'Changing the type changes which dataset is displayed. Switching back will restore the retained data.',
        ],
      },
    });
    setShowStructureModal(true);
  };

  const handleIntervalChange = (newValue, props) => {
    if (newValue === props.row.Interval) return;
    setPendingStructureChange({
      kind: 'interval',
      row: props.row,
      field: 'Interval',
      newValue,
      impact: buildIntervalChangeImpact(props.row, newValue),
    });
    setShowStructureModal(true);
  };

  const handleConfirmStructureChange = async (mode = 'merge') => {
    if (!pendingStructureChange) return;

    const { kind, row, field, newValue } = pendingStructureChange;
    const updated_data = {
      rowIds: [row.id],
      updated: { [field]: newValue },
    };
    const structureMode = kind === 'planType' ? 'retain' : mode;
    const success = await updateStrategyPlanHeader(updated_data, structureMode);

    if (success) {
      setShowStructureModal(false);
      setPendingStructureChange(null);
    }
  };

  const handleCancelStructureChange = () => {
    setShowStructureModal(false);
    setPendingStructureChange(null);
  };

  const tableColumns = useMemo(() => {
    return [
      {
        key: 'id',
        name: 'No',
        width: 50,
        sortable: true,
        renderCell: (props) => {
          return (
            <div style={{ justifyContent: 'left', paddingLeft: 4 }}>
              {props.rowIdx + 1}
            </div>
          );
        },
      },
      {
        key: 'OrderId',
        name: '',
        width: 70,
        renderCell: (props) => {
          return (
            <div
              style={{
                marginTop: 5,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <IconButton onClick={() => handleOrderUp(props.row.id)}>
                <ExpandLessIcon />
              </IconButton>
              <IconButton onClick={() => handleOrderDown(props.row.id)}>
                <ExpandMoreIcon />
              </IconButton>
            </div>
          );
        },
      },
      {
        key: 'Name',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <b>Name</b>
            {sortColumns.find((column) => column.columnKey === 'Name')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find((column) => column.columnKey === 'Name')
                ?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        sortable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.Name}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    Name: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        renderEditCell: renderTextEditor,
        renderCell: (props) => {
          return (
            <div style={{ wordBreak: 'break-all', paddingLeft: 4 }}>
              {props.row.Name}
            </div>
          );
        },
      },
      {
        key: 'StartDate',
        name: 'Start Date',
        sortable: true,
        width: 150,
        renderCell: (props) => {
          return (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DemoContainer components={['DatePicker']}>
                <DatePicker
                  format="DD/MM/YY"
                  sx={{
                    minWidth: '149px !important',
                    marginTop: '-8px !important',
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                  }}
                  value={dayjs(props.row.StartDate)}
                  onChange={(newValue) => {
                    handleStartDateChange(newValue, props);
                  }}
                />
              </DemoContainer>
            </LocalizationProvider>
          );
        },
      },
      {
        key: 'EndDate',
        name: 'End Date',
        sortable: true,
        width: 150,
        renderCell: (props) => {
          return (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DemoContainer components={['DatePicker']}>
                <DatePicker
                  format="DD/MM/YY"
                  sx={{
                    minWidth: '149px !important',
                    marginTop: '-8px !important',
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                  }}
                  value={dayjs(props.row.EndDate)}
                  onChange={(newValue) => {
                    handleEndDateChange(newValue, props);
                  }}
                />
              </DemoContainer>
            </LocalizationProvider>
          );
        },
      },
      {
        key: 'PlanType',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <b>Plan Type</b>
            {sortColumns.find((column) => column.columnKey === 'PlanType')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find((column) => column.columnKey === 'PlanType')
                ?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 130,
        sortable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.PlanType}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    PlanType: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {planTypes.map((planType) => (
                  <option key={planType.id}>{planType.value}</option>
                ))}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => (
          <Select
            fullWidth
            variant="standard"
            value={props.row.PlanType}
            onChange={(e) => handlePlanTypeChange(e.target.value, props)}
          >
            {planTypes.map((planType) => (
              <MenuItem key={planType.id} value={planType.value}>
                {planType.value}
              </MenuItem>
            ))}
          </Select>
        ),
      },
      {
        key: 'Interval',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <b>Interval</b>
            {sortColumns.find((column) => column.columnKey === 'Interval')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find((column) => column.columnKey === 'Interval')
                ?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 130,
        sortable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.Interval}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    Interval: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {intervalTypes.map((intervalType) => (
                  <option key={intervalType.id} value={intervalType.value}>
                    {intervalType.value}
                  </option>
                ))}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => (
          <Select
            fullWidth
            variant="standard"
            value={props.row.Interval}
            onChange={(e) => handleIntervalChange(e.target.value, props)}
          >
            {intervalTypes.map((intervalType) => (
              <MenuItem key={intervalType.id} value={intervalType.value}>
                {intervalType.value}
              </MenuItem>
            ))}
          </Select>
        ),
      },
      {
        key: 'DimPlanHeaderKey_ParentID',
        name: 'Parent Plan',
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimPlanHeaderKey_ParentID}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimPlanHeaderKey_ParentID: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {rows?.map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.Name}
                  </option>
                ))}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={props.row.DimPlanHeaderKey_ParentID}
              onChange={(e) =>
                props.onRowChange(
                  { ...props.row, DimPlanHeaderKey_ParentID: e.target.value },
                  true,
                )
              }
            >
              <MenuItem value={0}>None</MenuItem>
              {rows
                ?.filter((row) => row.id !== props.row.id)
                .map((row) => (
                  <MenuItem key={row.id} value={row.id}>
                    {row.Name}
                  </MenuItem>
                ))}
            </Select>
          );
        },
      },
      {
        key: 'Status',
        name: (
          <Checkbox
            onChange={(ev) => {
              setFilters({
                ...filters,
                Status: !filters.Status,
              });
            }}
            checked={filters.Status}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            color="success"
          />
        ),
        width: 70,
        renderCell({ row, onRowChange, tabIndex }) {
          return (
            <Checkbox
              checked={Boolean(row.Status)}
              color="success"
              size="small"
              tabIndex={tabIndex}
              inputProps={{ 'aria-label': 'Active strategy plan' }}
              onChange={(event) => {
                onRowChange({ ...row, Status: event.target.checked });
              }}
              onClick={(event) => event.stopPropagation()}
            />
          );
        },
      },
    ];
    // eslint-disable-next-line
  }, [rows, filters, sortColumns]);

  const filteredRows = useMemo(() => {
    return rows?.filter((r) => {
      return (
        (filters.DimPlanHeaderKey_ParentID !== 'All'
          ? r.DimPlanHeaderKey_ParentID === filters.DimPlanHeaderKey_ParentID
          : true) &&
        (filters.PlanType !== 'All' ? r.PlanType === filters.PlanType : true) &&
        (filters.Interval !== 'All' ? r.Interval === filters.Interval : true) &&
        (filters.Name
          ? r.Name?.toLowerCase().includes(filters.Name.toLowerCase())
          : true) &&
        (filters.Status === true ? r.Status === true : true)
      );
    });
  }, [rows, filters]);

  const sortedRows = useMemo(() => {
    if (sortColumns.length === 0) return filteredRows;

    return [...filteredRows].sort((a, b) => {
      for (const sort of sortColumns) {
        const comparator = getComparator(sort.columnKey);
        const compResult = comparator(a, b);
        if (compResult !== 0) {
          return sort.direction === 'ASC' ? compResult : -compResult;
        }
      }
      return 0;
    });
  }, [filteredRows, sortColumns]);

  const activeSortedRows = useMemo(
    () => (sortedRows ? sortedRows.filter((row) => row.Status) : []),
    [sortedRows],
  );

  const tabIdx = _.findIndex(activeSortedRows, { id: selectedPlanId });
  const selectedPlanHeader = planHeaders?.find(
    (header) => header.id === selectedPlanId,
  );

  const maxHeight = useMemo(() => {
    let max_height = window.innerHeight - 134;
    if (companyLogo) max_height -= 20;
    if (byLine) max_height -= 57;

    return max_height;
  }, [companyLogo, byLine]);

  return (
    <div className="StrategyContent">
      <AppBar position="relative" color="default">
        <Toolbar>
          <IconButton onClick={() => navigate('/')}>
            <HouseIcon />
          </IconButton>
          
          <div style={{ width: 'calc(100% - 80px)' }}>
            <Tabs
              value={tabIdx < 0 ? 0 : tabIdx + 1}
              onChange={handleTableRoutes}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label="List" id={0} />
              {activeSortedRows &&
                activeSortedRows.map((row, idx) => (
                  <Tab label={row.Name} id={idx + 1} key={idx + 1} />
                ))}
            </Tabs>
          </div>

          <IconButton onClick={onSignout} className="sign-out-but">
            <ExitToAppIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      {selectedPlanId === 0 ? (
        <div style={{ position: 'relative' }}>
          <Box display="flex" alignItems="flex-end" justifyContent="space-between" my={0.5}>
            <Box display="flex" flexDirection="column" alignItems="flex-start" gap={0.5} ml={1}>
              {companyLogo ? (
                <img
                  src={`data:image/png;base64,${companyLogo}`}
                  alt="company_logo"
                  style={{
                    height: 60,
                    width: 'fit-content',
                  }}
                />
              ) : null}
              {byLine ? (
                <Box display="flex">
                  <p
                    style={{
                      float: 'left',
                      padding: '10px 5px',
                      margin: 0,
                      background: 'rgb(224, 224, 224)',
                      fontSize: 17,
                    }}
                  >
                    Our Why
                  </p>
                  <div className="by_line" style={{ fontSize: 17 }}>
                    {ReactHtmlParser(byLine)}
                  </div>
                </Box>
              ) : null}
            </Box>

            <Box display="flex" alignItems="center" gap={1}>
              <StrategyExportButton
                tableHeight={maxHeight}
                onExport={() =>
                  strategyExportToPdf(
                    `${
                      planHeaders?.find((header) => header.id === selectedPlanId)
                        ?.Name
                    }.pdf`,
                  )
                }
              >
                Export to PDF
              </StrategyExportButton>
              <IconButton aria-label="Add" onClick={onClickedAdd}>
                <AddIcon />
              </IconButton>
              <IconButton aria-label="Search" onClick={toggleFilters}>
                <SearchIcon />
              </IconButton>
              <IconButton aria-label="Delete" onClick={onClickedDelete}>
                <DeleteIcon />
              </IconButton>
            </Box>
          </Box>

          <FilterContext.Provider value={filters}>
            <DataGrid
              rowKeyGetter={rowKeyGetter}
              className={
                filters.enabled
                  ? 'filter-container strategy_table fill-grid rdg-light'
                  : 'strategy_table fill-grid rdg-light'
              }
              columns={tableColumns}
              rows={sortedRows}
              headerRowHeight={filters.enabled ? 70 : 50}
              rowClass={(row, index) =>
                index % 2 === 1 ? 'rdg-highlight-cell' : undefined
              }
              sortColumns={sortColumns}
              onSortColumnsChange={setSortColumns}
              onRowsChange={handleRowChange}
              onCellClick={handleCellClick}
              onCopy={handleCopy}
              onPaste={handlePaste}
              rowHeight={50}
              style={{ height: maxHeight, resize: 'both' }}
            />
          </FilterContext.Provider>
        </div>
      ) : !selectedPlanHeader ? (
        <div className="strategy-plan" />
      ) : selectedPlanHeader.PlanType === 'Objective' ? (
        <div className="strategy-plan">
          <ObjectiveTable
            selectedPlanId={selectedPlanId}
            selParentId={selParentId}
            planHeaders={planHeaders}
            onRouteBack={onRouteBack}
            handleShowChildren={handleShowChildren}
            handleSelParentIdChange={handleSelParentIdChange}
            companyLogo={companyLogo}
            byLine={byLine}
          />
        </div>
      ) : (
        <div className="strategy-plan">
          <MileStoneTable
            selectedPlanId={selectedPlanId}
            selParentId={selParentId}
            planHeaders={planHeaders}
            onRouteBack={onRouteBack}
            handleSelParentIdChange={handleSelParentIdChange}
            companyLogo={companyLogo}
            byLine={byLine}
          />
        </div>
      )}

      <Dialog
        open={showStructureModal}
        onClose={handleCancelStructureChange}
        aria-labelledby="strategy-structure-dialog-title"
        aria-describedby="strategy-structure-dialog-description"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="strategy-structure-dialog-title">
          {pendingStructureChange?.kind === 'planType'
            ? 'Confirm Plan Type Change'
            : pendingStructureChange?.kind === 'interval'
              ? 'Change Strategy Interval'
              : 'Change Strategy Date Range'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="strategy-structure-dialog-description"
            component="div"
          >
            {pendingStructureChange?.kind === 'planType'
              ? 'This is a structural change to the Strategy plan.'
              : 'Choose how any existing milestone-period data should be handled:'}
            <Box component="ul" sx={{ pl: 2, mt: 1, mb: 0 }}>
              {pendingStructureChange?.impact?.messageLines?.map((line, index) => (
                <li key={index}>{line}</li>
              ))}
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelStructureChange}>
            Cancel
          </Button>
          {pendingStructureChange?.kind !== 'planType' && (
            <Button
              onClick={() => handleConfirmStructureChange('delete')}
              color="error"
            >
              {pendingStructureChange?.kind === 'interval'
                ? 'Start Fresh'
                : 'Remove Affected Data'}
            </Button>
          )}
          <Button
            onClick={() => handleConfirmStructureChange('merge')}
            color="primary"
            variant="contained"
          >
            {pendingStructureChange?.kind === 'planType'
              ? 'Change & Keep Data'
              : 'Preserve & Merge'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default Strategy;
